import { DateTime } from "luxon"
import moment from "moment"

// General Timestamp Handling Utility Functions

const use_luxon = false
const FORMATS = {
  LUXON: {
    DATE: "yyyy-LL-dd",
    DATETIME: "yyyy-LL-dd HH:mm:ss",
    DATETIME_NO_SEC: "yyyy-LL-dd HH:mm",
    DATETIME_TZ: "yyyy-LL-dd HH:mm:ss ZZZ",
    DATETIME_NO_SEC_TZ: "yyyy-LL-dd HH:mm ZZZ",
  },
  MOMENT: {
    DATE: "YYYY-MM-DD",
    DATETIME: "YYYY-MM-DD HH:mm:ss",
    DATETIME_NO_SEC: "YYYY-MM-DD HH:mm",
    DATETIME_TZ: "YYYY-MM-DD HH:mm:ss ZZ",
    DATETIME_NO_SEC_TZ: "YYYY-MM-DD HH:mm ZZ",
  },
}

export function parseDate(str) {
  if (!str) return str

  /*
   * Casting to Date() constructor first for Luxon to get valid timestamp localization
   * interpretation to the tz the current user is in.
   *
   * Doing .valueOf() to make a copy of any Date objects passed so we don't directly edit the passed
   * variable and have a handle on a separate copy.
   * */
  const ts = use_luxon ? DateTime.fromJSDate(new Date(str.valueOf())) : new Date(str.valueOf())
  if (!isValidDate(ts)) return null
  return ts
}

export function getTimestampDifference(ts1, ts2, getAbsoluteDifference = true) {
  if (!isValidDate(parseDate(ts1)) || !isValidDate(parseDate(ts2))) return {}

  const diff = new Date(ts2.valueOf()).getTime() - new Date(ts1.valueOf()).getTime()
  let days = diff / (1000 * 60 * 60 * 24)
  if (days < 0 && getAbsoluteDifference) days = -1 * days

  return {
    days: days,
    hours: days * 24,
    minutes: days * 24 * 60,
  }
}

export function getCurrentTime() {
  return use_luxon ? DateTime.fromJSDate(new Date()) : new Date()
}

function isValidLuxonDate(ts) {
  return ts instanceof DateTime && !ts.invalid
}

export function isValidDate(ts) {
  if (!ts) return ts

  return isValidLuxonDate(ts) || (ts instanceof Date && !isNaN(ts))
}

export function adjustDateValue(date, metric, num, add = true) {
  if (!date) return date

  const ts = parseDate(date.valueOf()) // Don't want to modify passed value directly. Calling valueOf to only modify the copy
  if (!isValidDate(ts)) return ts

  if (isValidLuxonDate(ts)) {
    const key = `${metric}s`
    return add ? ts.plus({ [key]: num }) : ts.minus({ [key]: num })
  } else {
    let offset = add ? num : -1 * num
    if (metric === "month") {
      return new Date(ts.setMonth(ts.getMonth() + offset))
    } else if (metric === "minute" || metric === "hour") {
      if (metric === "hour") {
        offset = offset * 60 // Convert hour to minutes
      }
      return ts.setTime(ts.getTime() + offset * 60000)
    } else {
      // Days adjustment
      return new Date(ts.setDate(ts.getDate() + offset))
    }
  }
}

export function convertTZ(date, tzString = null, getString = true) {
  const ts = date && !isValidDate(date) ? parseDate(date) : date
  if (!ts || !isValidDate(ts)) return ts

  // If no passed tzString return either the passed Date obj or the string version of the date
  if (!tzString) return getString ? stringifyDate(ts) : ts

  // Adjust timezone of date object
  const adj_ts = isValidLuxonDate(ts) ? ts.setZone(tzString) : DateTime.fromJSDate(ts).setZone(tzString)

  // If tzString passed is invalid, the setZone ts conversion will not be applied and we proceed with the passed ts
  const mod_ts = stringifyDate(isValidLuxonDate(adj_ts) ? adj_ts : ts)

  return getString
    ? mod_ts // Returns the string version of the new tz timestamp
    : moment(mod_ts) // Returns a moment Object that assumes local tz, BUT the date will look to the user as belonging to the tz selected
}

export function stringifyDate(date, show_time = true, show_tz = false, show_sec = true) {
  let dateFormat
  const ts = date && !isValidDate(date) ? parseDate(date) : date
  const isLuxonDate = isValidLuxonDate(ts)
  const formatType = isLuxonDate ? "LUXON" : "MOMENT"

  if (!isValidDate(ts)) return ts

  if (!show_time) {
    dateFormat = FORMATS[formatType].DATE
  } else if (show_tz) {
    dateFormat = show_sec ? FORMATS[formatType].DATETIME_TZ : FORMATS[formatType].DATETIME_NO_SEC_TZ
  } else {
    dateFormat = show_sec ? FORMATS[formatType].DATETIME : FORMATS[formatType].DATETIME_NO_SEC
  }

  return isLuxonDate ? ts.toFormat(dateFormat) : moment(ts).format(dateFormat)
}

export function sortDates(arr = [], sort_key = "timestamp", asc = true) {
  // Sorting works the same in both native Date() constructor and luxon timestamp objects
  const newArr = Object.assign([], arr)

  // Returns timestamps in ascending order
  if (asc) return newArr.sort((a, b) => a[sort_key].valueOf() - b[sort_key].valueOf())
  // Returns timestamps in descending order
  return newArr.sort((a, b) => b[sort_key].valueOf() - a[sort_key].valueOf())
}

// ---- General Timestamp Handling Utility Functions

// General JS Utility Functions

export function arrayWithContents(array) {
  return !!(array && Array.isArray(array) && array.length > 0)
}

export function isNumber(num) {
  if (typeof num === "string") {
    return num.trim() !== "" && !isNaN(num)
  }
  return (num || num === 0) && !isNaN(num)
}

export const hideModal = (setModal) => {
  setModal(false)
}

export const showModal = (setModal) => {
  setModal(true)
}

export const deepcopy = (obj) => {
  if (obj) {
    return JSON.parse(JSON.stringify(obj))
  } else {
    return obj
  }
}

/*
 * Provided with a uri and a name, triggers a download
 * of the file at the uri.
 */
export const triggerDownload = (uri, filename = "download") => {
  const a = document.createElement("a")

  a.setAttribute("download", filename)
  a.setAttribute("href", uri)
  a.setAttribute("target", "_blank")

  a.dispatchEvent(
    new MouseEvent("click", {
      view: window,
      bubbles: false,
      cancelable: true,
    }),
  )
}

// converts "Some Field" into "is_some_field" - Used on LTPM
export const stringifyGroupName = (group) => {
  return "is_" + group.name.toLowerCase().replace(/ /g, "_")
}

// ---- General JS Utility Functions

// whether two arrays have same elements irregardless of order
export const equalArrays = (arr1 = [], arr2 = []) => {
  if (arr1.length !== arr2.length) return false
  return arr1.every((elem) => arr2.includes(elem))
}

// ---- Logging

export function log_js_error(message) {
  fetch("/backend/logger", {
    method: "POST",
    headers: new Headers({
      "X-CSRFToken": window.application_csrf_token,
    }),
    body: message,
    credentials: "same-origin",
  })
}

// ---- Event handlers

export const preventDefault = (event) => event.preventDefault()

export function titleCase(str = "") {
  return str
    .toLowerCase()
    .split(" ")
    .map(function (word) {
      return word.replace(word[0], word[0].toUpperCase())
    })
    .join(" ")
}

export const sentencify = (txt) => {
  return titleCase(txt.replaceAll("_", " "))
}

export const chunkList = (array, chunk_size) => {
  // Utility function to chunk an array into an array of arrays of a given chunk size
  let chunked_array = []
  for (let i = 0; i < array.length; i += chunk_size) {
    chunked_array.push(array.slice(i, i + chunk_size))
  }
  return chunked_array
}
